import React, { useEffect, useState } from 'react';
import './TakeTest.css';

const TestWaitingPage = ({ testName, testDuration, testMarks, scheduledTime, onTimeReached }) => {
  const [countdown, setCountdown] = useState('');
  const [timeStatus, setTimeStatus] = useState('waiting'); // 'waiting', 'starting', 'closed'

  useEffect(() => {
    const updateCountdown = () => {
      const now = new Date().getTime();
      const scheduled = new Date(scheduledTime).getTime();
      const startTime = scheduled + (5 * 60 * 1000); // scheduled_time + 5 minutes
      const diff = startTime - now;

      if (diff <= 0) {
        setTimeStatus('starting');
        if (onTimeReached) onTimeReached();
      } else {
        const mins = Math.floor(diff / 60000);
        const secs = Math.floor((diff % 60000) / 1000);
        setCountdown(`${mins}:${secs < 10 ? '0' : ''}${secs}`);
        setTimeStatus('waiting');
      }
    };

    updateCountdown();
    const timer = setInterval(updateCountdown, 1000);
    return () => clearInterval(timer);
  }, [scheduledTime, onTimeReached]);

  const startDate = new Date(scheduledTime);
  startDate.setMinutes(startDate.getMinutes() + 5);

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Icon */}
        <div style={styles.icon}>⏱</div>

        {/* Title */}
        <h1 style={styles.title}>Your test will start soon</h1>

        {/* Test Name */}
        <p style={styles.testName}>{testName}</p>

        {/* Test Details */}
        <div style={styles.details}>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Duration</span>
            <span style={styles.detailValue}>{testDuration} mins</span>
          </div>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Total Marks</span>
            <span style={styles.detailValue}>{testMarks}</span>
          </div>
        </div>

        {/* Scheduled Time */}
        <div style={styles.timeBlock}>
          <p style={styles.timeLabel}>Test will start at:</p>
          <p style={styles.time}>
            {startDate.toLocaleTimeString('en-IN', { 
              hour: '2-digit', minute: '2-digit', second: '2-digit',
              hour12: true
            })}
          </p>
          <p style={styles.date}>
            {startDate.toLocaleDateString('en-IN', { 
              day: '2-digit', month: 'short', year: 'numeric'
            })}
          </p>
        </div>

        {/* Countdown */}
        {timeStatus === 'waiting' && (
          <div style={styles.countdownBlock}>
            <p style={styles.countdownLabel}>Starts in</p>
            <p style={styles.countdownTimer}>{countdown}</p>
          </div>
        )}

        {/* Starting Message */}
        {timeStatus === 'starting' && (
          <div style={styles.startingBlock}>
            <p style={styles.startingText}>🚀 Test is starting...</p>
          </div>
        )}

        {/* Info Message */}
        <div style={styles.infoBox}>
          <p style={styles.infoText}>
            ℹ️ Please wait. The test will start automatically when the time arrives.
          </p>
          <p style={{ ...styles.infoText, marginTop: 8 }}>
            Do not close this page.
          </p>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: '20px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  card: {
    background: 'white',
    borderRadius: '12px',
    padding: '40px',
    maxWidth: '500px',
    width: '100%',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    textAlign: 'center',
  },
  icon: {
    fontSize: '48px',
    marginBottom: '20px',
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#2c3e50',
    margin: '0 0 8px 0',
  },
  testName: {
    fontSize: '16px',
    color: '#7f8c8d',
    margin: '0 0 24px 0',
    fontWeight: '500',
  },
  details: {
    display: 'flex',
    justifyContent: 'space-around',
    marginBottom: '30px',
    padding: '16px',
    background: '#f8f9fa',
    borderRadius: '8px',
  },
  detailItem: {
    display: 'flex',
    flexDirection: 'column',
  },
  detailLabel: {
    fontSize: '12px',
    color: '#95a5a6',
    fontWeight: '600',
    textTransform: 'uppercase',
    marginBottom: '4px',
  },
  detailValue: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#2c3e50',
  },
  timeBlock: {
    background: '#e8f4f8',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '24px',
    border: '2px solid #b3dfe0',
  },
  timeLabel: {
    fontSize: '13px',
    color: '#5a7a7c',
    fontWeight: '600',
    margin: '0 0 8px 0',
  },
  time: {
    fontSize: '32px',
    fontWeight: '700',
    color: '#1099a8',
    margin: '0',
    fontVariantNumeric: 'tabular-nums',
  },
  date: {
    fontSize: '14px',
    color: '#5a7a7c',
    margin: '8px 0 0 0',
  },
  countdownBlock: {
    background: '#fff3cd',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '24px',
    border: '2px solid #ffc107',
  },
  countdownLabel: {
    fontSize: '13px',
    color: '#856404',
    fontWeight: '600',
    textTransform: 'uppercase',
    margin: '0 0 8px 0',
  },
  countdownTimer: {
    fontSize: '36px',
    fontWeight: '700',
    color: '#ff6b6b',
    margin: '0',
    fontVariantNumeric: 'tabular-nums',
    fontFamily: 'monospace',
  },
  startingBlock: {
    background: '#d4edda',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '24px',
    border: '2px solid #28a745',
  },
  startingText: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#155724',
    margin: '0',
  },
  infoBox: {
    background: '#e3f2fd',
    borderRadius: '8px',
    padding: '16px',
    border: 'left-edge: 4px solid #2196f3',
  },
  infoText: {
    fontSize: '13px',
    color: '#1565c0',
    margin: '0',
    lineHeight: '1.6',
  },
};

export default TestWaitingPage;
